import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import type { StepRenderProps } from "@/components/onboarding/components/WelcomeWizard/WelcomeWizard.types";
import {
  CheckableOption,
  FooterContainer,
  FooterSkipButton,
  OptionsSection,
  StepContent,
  StepTitle,
  WizardContentWrapper,
  FooterButton,
} from "@/components/onboarding/components/WelcomeWizard/WelcomeWizard.styles";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { openWindowUrlInNewTab } from "@/modules/window/navigation";
import { APP_CONFIG } from "@/domains/global/config";
import { MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";

export const DownloadAppsStep: React.FC<StepRenderProps> = ({ onNavToStep }) => {
  const handleInstallExtension = async () => {
    trackEvent(TrackedEvent.ImportWizardStepChange, {
      from: "download-apps",
      to: "external_link",
      source: "welcome_wizard",
    });
    trackEvent(TrackedEvent.ChromeExtensionOnboardingInstallerClick);
    await openWindowUrlInNewTab({ url: new URL(APP_CONFIG.URLS.CHROME_EXTENSION) });
  };

  const handleDownloadMacApp = async () => {
    trackEvent(TrackedEvent.ImportWizardStepChange, {
      from: "download-apps",
      to: "download-mac-app",
      source: "welcome_wizard",
    });
    await openWindowUrlInNewTab({ url: new URL(APP_CONFIG.URLS.MAC_APP) });
  };

  const handleDownloadiOSApp = async () => {
    trackEvent(TrackedEvent.ImportWizardStepChange, {
      from: "download-apps",
      to: "download-ios-app",
      source: "welcome_wizard",
    });
    await openWindowUrlInNewTab({ url: new URL(APP_CONFIG.URLS.IOS_APP_V2) });
  };

  const handleNext = () => {
    onNavToStep?.("intro-video");
  };

  useEffect(() => {
    trackEvent(TrackedEvent.ChromeExtensionOnboardingInstallerView);
  }, []);

  return (
    <WizardContentWrapper>
      <StepTitle>Get the Mem apps</StepTitle>

      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          Record meetings, capture thoughts on-the-go, and save web pages. Mem turns everything into
          organized, searchable notes.
        </MDSText>

        <OptionsSection>
          <CheckableOption isSelected={false} onClick={handleDownloadMacApp}>
            <OptionWrapper>
              <MdsIcon kind={MdsIconKind.BrandApple} />
              <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                Download Mac app
              </MDSText>
            </OptionWrapper>
          </CheckableOption>
          <CheckableOption isSelected={false} onClick={handleDownloadiOSApp}>
            <OptionWrapper>
              <MdsIcon kind={MdsIconKind.Mobile} />
              <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                Download iOS app
              </MDSText>
            </OptionWrapper>
          </CheckableOption>
          <CheckableOption isSelected={false} onClick={handleInstallExtension}>
            <OptionWrapper>
              <MdsIcon kind={MdsIconKind.GoogleChrome} />
              <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                Install Chrome extension
              </MDSText>
            </OptionWrapper>
          </CheckableOption>
        </OptionsSection>
      </StepContent>

      <FooterContainer align="space-between">
        <FooterSkipButton onClick={handleNext}>Skip for now</FooterSkipButton>
        <FooterButton onClick={handleNext}>Next</FooterButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
};

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;

  .mds-icon {
    color: ${({ theme }) => theme.colors.icon.primary};
    font-size: 16px;
    height: 16px;
    width: 16px;
  }
`;
